import { Search } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useI18n } from '@/lib/i18n';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  DWA OKNA DO BŁĘDÓW W PODGLĄDZIE IMPORTU — zamówione 08.09.2026.
 *
 *  ⚠️⚠️ OBA POWSTAŁY Z TEJ SAMEJ OBSERWACJI: podgląd mówił, CO jest źle, i na tym kończył.
 *    • „po najechaniu na wiersz z błędem chciałbym mieć możliwość kliknięcia na ten numer karty
 *      i podgląd kto aktualnie jej używa + […] możliwość nadpisania osoby (tak jak mówiliśmy —
 *      czasami osoba dostaje kartę po kimś innym)";
 *    • „w przypadku błędu grupy — chciałbym móc kliknąć na to i mieć podgląd grup jakie w ogóle
 *      występują, w tej chwili, żeby poprawić grupę to trzeba z pamięci wiedzieć jakie są".
 *
 *  ⚠️ DYMEK NIE WYSTARCZA i to jest tu sedno: „Ten numer karty jest już używany na wyspie"
 *  odpowiada na pytanie „czy coś jest źle", a nie na jedyne, które w tym momencie ma znaczenie —
 *  „przez kogo" i „co z tym zrobić".
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

/** Pracownik, który już siedzi na tym numerze karty — tyle, ile widać na liście kadry. */
export type ZajetaKarta = {
  numer: string;
  nazwa: string;
  alias: string | null;
  centrum: string | null;
  aktywny: boolean;
  grupy: string[];
};

/**
 * KTO UŻYWA TEJ KARTY — i co z tym można zrobić.
 *
 * ⚠️⚠️ NADPISANIE JEST DECYZJĄ NA CAŁY WSAD, nie na ten jeden wiersz, i okno musi to powiedzieć.
 * `Rationing`… a właściwie `Users` w SPS nie ma pojęcia „nadpisz tylko tego" — nasz import ma
 * jeden przełącznik, który zamienia KAŻDY wiersz z zajętą kartą w aktualizację. Napis „nadpisz
 * tę osobę" przy globalnym skutku byłby kłamstwem w miejscu, w którym kłamstwo kosztuje kadrę.
 */
export function OknoZajetejKarty({
  karta,
  ileZajetych,
  nadpisuj,
  onNadpisuj,
  onClose,
}: {
  karta: ZajetaKarta | null;
  ileZajetych: number;
  nadpisuj: boolean;
  onNadpisuj: (v: boolean) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent title={t('imp.zajeta.tytul')} description={t('imp.zajeta.opis')}>
        {karta === null ? (
          /**
           * ⚠️ MOŻE SIĘ ZDARZYĆ I NIE JEST BŁĘDEM: listę kadry ciągniemy do 500 osób, a kolizję
           * powyżej tego wyłapuje instancja. Wtedy wiemy, że numer jest zajęty, ale nie wiemy
           * przez kogo — i trzeba to powiedzieć, a nie pokazać puste pola.
           */
          <p className="text-sm text-muted-foreground">{t('imp.zajeta.nieWiemyKto')}</p>
        ) : (
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
            <dt className="text-muted-foreground">{t('imp.pole.karta')}</dt>
            <dd className="font-mono">{karta.numer}</dd>
            <dt className="text-muted-foreground">{t('imp.pole.nazwa')}</dt>
            <dd className="font-medium">{karta.nazwa === '' ? t('imp.zajeta.bezNazwy') : karta.nazwa}</dd>
            <dt className="text-muted-foreground">{t('imp.pole.alias')}</dt>
            <dd>{karta.alias === null || karta.alias === '' ? '—' : karta.alias}</dd>
            <dt className="text-muted-foreground">{t('imp.pole.centrum')}</dt>
            <dd>{karta.centrum === null || karta.centrum === '' ? '—' : karta.centrum}</dd>
            <dt className="text-muted-foreground">{t('imp.zajeta.konto')}</dt>
            <dd>
              {karta.aktywny ? (
                <Badge variant="success">{t('imp.zajeta.aktywne')}</Badge>
              ) : (
                <Badge variant="warning">{t('imp.zajeta.zablokowane')}</Badge>
              )}
            </dd>
            <dt className="text-muted-foreground">{t('imp.pole.grupa')}</dt>
            <dd>{karta.grupy.length === 0 ? '—' : karta.grupy.join(', ')}</dd>
          </dl>
        )}

        {/*
          ⚠️⚠️ PRZEŁĄCZNIK MÓWI, ILU WIERSZY DOTYCZY. Import ma jeden przełącznik nadpisywania na
          cały wsad, więc włączenie go tutaj zamienia w aktualizację KAŻDY wiersz z zajętą kartą.
          Liczba przy nim jest jedyną rzeczą, która broni przed przepisaniem pół kadry przy
          poprawianiu jednego wiersza.
        */}
        <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2 text-xs">
          <input
            type="checkbox"
            className="mt-0.5 size-3.5"
            checked={nadpisuj}
            onChange={(e) => onNadpisuj(e.target.checked)}
          />
          <span>
            <span className="font-medium">{t('imp.zajeta.nadpisuj')}</span>
            <span className="block text-[11px] text-muted-foreground">
              {t('imp.zajeta.nadpisujIle').replace('{ile}', String(ileZajetych))}
            </span>
          </span>
        </label>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * JAKIE GRUPY SĄ NA TEJ WYSPIE — i wpisanie wybranej w ten wiersz.
 *
 * ⚠️⚠️ LISTA WYSTARCZA DO PATRZENIA, NIE DO POPRAWIENIA. Dlatego kliknięcie w grupę wpisuje jej
 * KOD w tę linijkę wklejki (`podmienPole`), a nie tylko pokazuje nazwę do przepisania ręcznie —
 * przepisywanie z ekranu do pola obok jest dokładnie tą pracą, którą to okno ma zdjąć.
 *
 * ⚠️ PRZY ARKUSZU WPISAĆ NIE MA GDZIE: tam grupa przychodzi z KOLUMNY pliku, więc okno pokazuje
 * samą listę i mówi wprost, że poprawka idzie przez mapowanie kolumn. Cichy brak reakcji na klik
 * byłby gorszy od braku klikalności.
 */
export function OknoWyboruGrupy({
  grupy,
  wpisane,
  mozeWpisac,
  onWybierz,
  onClose,
}: {
  grupy: readonly { id: string; nazwa: string; kod: string; osob: number | null }[];
  /** Co stoi w tym wierszu teraz — żeby było widać, czego szukaliśmy. */
  wpisane: string;
  mozeWpisac: boolean;
  onWybierz: (kod: string) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [szukaj, setSzukaj] = useState('');
  const fraza = szukaj.trim().toLowerCase();
  const widoczne =
    fraza === ''
      ? grupy
      : grupy.filter((g) => g.nazwa.toLowerCase().includes(fraza) || g.kod.toLowerCase().includes(fraza));

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title={t('imp.grupy.tytul')}
        description={t('imp.grupy.opis').replace('{co}', wpisane === '' ? '—' : wpisane)}
      >
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            className="pl-8"
            placeholder={t('imp.grupy.szukaj')}
            value={szukaj}
            onChange={(e) => setSzukaj(e.target.value)}
          />
        </div>

        {grupy.length === 0 ? (
          /** ⚠️ Wyspa bez grup to realny stan (świeży klient) — mówimy to, zamiast pokazać pustkę. */
          <p className="text-sm text-muted-foreground">{t('imp.grupy.brakNaWyspie')}</p>
        ) : (
          <ul className="max-h-[20rem] divide-y divide-border overflow-y-auto rounded-md border border-border">
            {widoczne.map((g) => (
              <li key={g.id}>
                {mozeWpisac ? (
                  <button
                    type="button"
                    className="flex w-full items-baseline justify-between gap-2 px-2.5 py-1.5 text-left text-sm hover:bg-accent"
                    onClick={() => onWybierz(g.kod)}
                  >
                    <span className="min-w-0 truncate">{g.nazwa}</span>
                    <span className="flex shrink-0 items-baseline gap-2">
                      {g.osob !== null && (
                        <span className="text-[11px] tabular-nums text-muted-foreground">
                          {t('imp.grupy.osob').replace('{ile}', String(g.osob))}
                        </span>
                      )}
                      <code className="text-[11px] text-muted-foreground">{g.kod}</code>
                    </span>
                  </button>
                ) : (
                  <div className="flex items-baseline justify-between gap-2 px-2.5 py-1.5 text-sm">
                    <span className="min-w-0 truncate">{g.nazwa}</span>
                    <code className="text-[11px] text-muted-foreground">{g.kod}</code>
                  </div>
                )}
              </li>
            ))}
            {widoczne.length === 0 && (
              <li className="px-2.5 py-1.5 text-xs text-muted-foreground">{t('imp.grupy.nicNiePasuje')}</li>
            )}
          </ul>
        )}

        {/* ⚠️ Przy arkuszu klik nic nie wpisze — mówimy to, zamiast pozwolić klikać bez skutku. */}
        {!mozeWpisac && <p className="text-xs text-muted-foreground">{t('imp.grupy.tylkoPodglad')}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
